import { useEffect, useRef } from "react";
import { Progress } from "@/components/ui/progress";
import { useApp } from "@/hooks/use-app-context";
import { useToast } from "@/hooks/use-toast";
import { useValidationGate } from "../useValidationGate";

/**
 * Bande de progression affichee sur les pages de validation lorsque le
 * compte connecte est bloque. Detecte la transition bloque -> debloque
 * pour afficher un toast, sans navigation automatique.
 */
export function ValidationGateProgressStrip({ className }: { className?: string }) {
  const { t } = useApp();
  const { toast } = useToast();
  const gate = useValidationGate();
  const wasBlockedRef = useRef(false);

  useEffect(() => {
    if (gate.isBlocked) {
      wasBlockedRef.current = true;
    } else if (wasBlockedRef.current) {
      wasBlockedRef.current = false;
      toast({ title: t.validationGateUnlockedTitle, description: t.validationGateUnlockedDesc });
    }
  }, [gate.isBlocked, t, toast]);

  if (!gate.isBlocked) return null;

  return (
    <div className={`rounded-lg border border-brand/30 bg-brand/[0.05] px-4 py-3 ${className ?? ""}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">
          {t.validationGateProgress
            .replace("{done}", String(gate.validationsCompleted))
            .replace("{total}", String(gate.validationsNeeded))}
        </span>
        <span className="tabular-nums text-muted-foreground">{gate.progressPercent}%</span>
      </div>
      <Progress value={gate.progressPercent} className="mt-2" />
    </div>
  );
}
